import React, { useCallback, useState } from 'react'
import { withRouter } from 'react-router'
import Image from 'components/image'
import classNames from 'classnames'
import { useCollectionFacets } from 'store/api/hooks'
import { constructFilterQueryString } from 'store/lib'
import { track, TrackVisibility } from 'lib/analytics'
import { filterEmptyFilters } from 'store/components/product-filters/helpers'
import { currentUserLoadedPromise } from 'shared/utils/currentUserLoadedPromise/currentUserLoadedPromise'
import { Facet } from '../FacetControl/Types'
import css from './BrandTiles.styles.scss'

interface SelectedFilters {
  [key: string]: string[] | undefined
}

interface BrandTilesProps {
  categorySlug: string
  categoryTitle: string
  router: any
  selectedFilters: SelectedFilters
  setSelectedFilters: React.Dispatch<React.SetStateAction<SelectedFilters>>
}

interface FeaturedBrand {
  label: string
  value: string
  count: number
  isFeatured: boolean
  brandImageUrl: string
}

const BrandTiles: React.FC<BrandTilesProps> = ({
  categorySlug,
  categoryTitle,
  router,
  selectedFilters,
  setSelectedFilters,
}) => {
  const [selectedBrandTile, setSelectedBrandTile] = useState(-1)

  // @ts-ignore:next-line
  const { data, isLoading } = useCollectionFacets(
    'category',
    categorySlug,
    selectedFilters
  )

  const featuredBrands = isLoading
    ? []
    : data.facets.filter(
        (facet: Facet<FeaturedBrand>) => facet.key === 'featured_brands'
      )

  const applyFilters = useCallback((test) => {
    const nonEmptyFilters = filterEmptyFilters(test)
    const filterQueryString = constructFilterQueryString(nonEmptyFilters)
    const queryString = [filterQueryString, ''].filter(Boolean).join('&')
    router.push(`${window.location.pathname}?${queryString}`)
  }, [])

  const handleFilterChange = useCallback(
    (idx) => {
      setSelectedBrandTile(idx)
      setSelectedFilters((prevFilters) => {
        const newFilters = { ...prevFilters }
        newFilters.brand = [featuredBrands[0]?.values[idx].value]
        applyFilters(newFilters)
        return newFilters
      })
    },
    [featuredBrands, selectedBrandTile]
  )

  const trackBrandTilesViewed = async (idx: number, brand: string) => {
    const currentUser = await currentUserLoadedPromise
    track.brandTileViewed({
      brand,
      storeCategoryName: categoryTitle,
      storeCategorySlug: categorySlug,
      eventCta: 'View Brand Tile',
      eventLocation: track.EventLocation.SHOP,
      eventType: track.EventType.NONE,
      isRegistryOwner:
        currentUser?.currentRegistry?.currentUserRole === 'owner',
      registryOwnerId: currentUser?.currentRegistry?.ownerId,
      registryId: currentUser?.currentRegistry?.id,
      positionInList: idx + 1,
    })
  }

  const trackBrandTilesClicked = async (idx: number, brand: string) => {
    const currentUser = await currentUserLoadedPromise
    track.brandTileClicked({
      brand,
      storeCategoryName: categoryTitle,
      storeCategorySlug: categorySlug,
      eventCta: 'Click on Brand Tile',
      eventLocation: track.EventLocation.SHOP,
      eventType: track.EventType.NONE,
      isRegistryOwner:
        currentUser?.currentRegistry?.currentUserRole === 'owner',
      registryOwnerId: currentUser?.currentRegistry?.ownerId,
      registryId: currentUser?.currentRegistry?.id,
      positionInList: idx + 1,
    })
  }

  return featuredBrands.length > 0 ? (
    <div className={css.BrandTiles__background}>
      <div className={css.BrandTiles__brandTilesContainer}>
        {featuredBrands[0]?.values.map(
          (featuredBrand: FeaturedBrand, idx: number) => (
            <TrackVisibility
              eventProperties={{
                event: () => {
                  trackBrandTilesViewed(idx, featuredBrand.value)
                },
              }}
            >
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div
                className={css.BrandTiles__brandTile}
                key={featuredBrand.label}
                onClick={() => {
                  handleFilterChange(idx)
                  trackBrandTilesClicked(idx, featuredBrand.value)
                }}
              >
                <Image
                  alt={featuredBrand.label}
                  className={classNames(css.BrandTiles__brandTile__image)}
                  src={featuredBrand.brandImageUrl}
                />
              </div>
            </TrackVisibility>
          )
        )}
      </div>
    </div>
  ) : (
    <></>
  )
}

export default withRouter(BrandTiles)
